import { comparer } from "mobx";
import { observer } from "mobx-react-lite";
import React from "react";
import { BaseSelect } from "./BaseSelect";
import { useFormContext } from "./Form";
import { StaticMultipleValues } from "./StaticInput";
import { DEFAULT_ID, withFormWrapper } from "./withFormWrapper";
// stable so it can appear in hook dependency arrays without defeating them
const EMPTY_MULTI_VALUE = [];
/**
 * The original piston form (MobX) select behavior: reads and writes the
 * form value via `useFormContext`, resolves the selected option
 * structurally, and renders `BaseSelect` for the live control.
 */
export const LegacySelect = observer(({ options, components, name, onChange, multiple = false, disabled = false, autoFocus = false, loading = false, formatter, placeholder, uniqueId = DEFAULT_ID, label, staticFormatter, isClearable = true, dataId, dataLabel, dataTestId, ariaLabelledby, creatable = false, portal, }) => {
    const { values, formDisabled, isStatic } = useFormContext();
    const emptyValue = multiple ? EMPTY_MULTI_VALUE : null;
    const selectionValue = values.get(name);
    const currentValue = React.useMemo(() => {
        if (multiple) {
            return selectionValue
                ? selectionValue.map((v) => options.find((o) => comparer.structural(o.value, v)))
                : emptyValue;
        }
        const value = options.find((o) => comparer.structural(o.value, selectionValue));
        return value !== undefined ? value : emptyValue;
    }, [multiple, selectionValue, options, emptyValue]);
    const getValueOfSelected = React.useCallback((selected) => {
        if (selected === null || selected === undefined) {
            return emptyValue;
        }
        if (multiple) {
            return selected.map((o) => o.value);
        }
        return selected.value;
    }, [multiple, emptyValue]);
    const getLabelOfSelected = React.useCallback((selected) => {
        if (selected === null || selected === undefined) {
            return emptyValue;
        }
        if (multiple) {
            return selected.map((o) => o.label);
        }
        return selected.label;
    }, [multiple, emptyValue]);
    const getLabelsFromValues = React.useCallback((value, options) => {
        if (value === null || value === undefined) {
            return emptyValue;
        }
        if (multiple) {
            const selected = options.filter((o) => value.includes(o.value));
            return selected.map((o) => o.label);
        }
        const selected = options.find((o) => o.value === value);
        return selected ? selected.label : emptyValue;
    }, [multiple, emptyValue]);
    const handleChange = React.useCallback((selected) => {
        if (onChange) {
            const newValue = getValueOfSelected(selected);
            const oldValue = selectionValue;
            const newLabel = getLabelOfSelected(selected);
            const oldLabel = getLabelsFromValues(selectionValue, options);
            onChange(newValue, oldValue, newLabel, oldLabel);
        }
        values.set(name, getValueOfSelected(selected));
    }, [
        onChange,
        values,
        selectionValue,
        options,
        getValueOfSelected,
        getLabelOfSelected,
        getLabelsFromValues,
        name,
    ]);
    if (isStatic) {
        return (React.createElement(StaticMultipleValues, { label: label, name: name, staticFormatter: staticFormatter, isLoading: loading, options: options, multiple: multiple, dataId: dataId, dataLabel: dataLabel, dataTestId: dataTestId }));
    }
    return (React.createElement(BaseSelect, { ariaLabelledby: ariaLabelledby ?? `form-group-${name}-${uniqueId}`, ariaLabel: label, id: `form-group-${name}-${uniqueId}`, components: components, multiple: multiple, options: options, value: creatable ? undefined : currentValue, onChange: handleChange, isClearable: isClearable, formatter: formatter, disabled: disabled || formDisabled, autoFocus: autoFocus, loading: loading, placeholder: placeholder, creatable: creatable, dataId: dataId, dataTestId: dataTestId, menuPortalTarget: portal }));
});
export const Select = withFormWrapper({ WrappedComponent: LegacySelect });
